import { acquireLease, webLocksSupported } from '@eluve/navigator-utils';

import { AppointmentFileIdbRow } from './files-db';
import { getTabId } from './tab-id';

/**
 * Determines whether a file is eligible for sync by this tab.
 *
 * A file is eligible if:
 * 1. It has no ownedByTabId (legacy file, backwards compatible)
 * 2. It was created by this tab
 * 3. The tab that created it is no longer alive (orphan recovery)
 * 4. Web Locks API is not supported (treat as legacy — allow sync)
 *
 * Tab liveness is checked by probing the Web Lock `tab:{tabId}`.
 * If the lock can be acquired, the tab is dead and the file is orphaned.
 *
 * [ENG-3289]
 */
export const isOwnedOrOrphanedFile = async (
  file: AppointmentFileIdbRow,
): Promise<boolean> => {
  const { ownedByTabId } = file;

  // Legacy files (no tab ownership) are always eligible
  if (!ownedByTabId) {
    return true;
  }

  // Files owned by this tab are always eligible
  if (ownedByTabId === getTabId()) {
    return true;
  }

  // If Web Locks are not supported, we can't check tab liveness.
  // Treat as legacy and allow sync to prevent orphaned files from being stuck.
  if (!webLocksSupported()) {
    return true;
  }

  // For files owned by another tab, check if that tab is still alive
  // by probing its Web Lock. If we can acquire it, the tab is dead.
  let probeLock: Awaited<ReturnType<typeof acquireLease>> = null;
  try {
    probeLock = await acquireLease(`tab:${ownedByTabId}`);
  } catch {
    // If the lock probe fails (e.g., security policy), treat as legacy and allow sync
    // to prevent orphaned files from being permanently stuck.
    return true;
  }

  if (probeLock) {
    // Tab is dead — release the probe lock and allow sync
    probeLock.release();
    return true;
  }

  // Tab is still alive — skip this file
  return false;
};
